export default class DateTimeClass {
	constructor(i18n) {
		/*This assigns the value of the i18n parameter to the i18n property of the object being created by the constructor.
		This means that when a new object is created using this constructor, the i18n parameter can be accessed using the this.i18n property.
		By using a constructor function to create objects with this property, multiple instances of the object can be created with different i18n values of an application.
		This is done to provide different localisations to the user */
		this.i18n = i18n;
	}

	getDateObjectFromTimeStamp(timestamp) {
		//Converts a timestamp to a date
		var dateobj = new Date(timestamp * 1000);
		return dateobj;
	}

	getDateObjectFromISODateTime(isodate) {
		//If an ISODateTime exists, it convertis it to a date. If not, it returns null.

		if(isodate){
			/*This splits the isodate string (on the space character) into the date and time parts.
			The result is stored in the splitdatetime variable.*/
			var splitdatetime = isodate.split(" ");

			/*Similar to the splitdatetime variable. However, in this instance, the split takes place on the dash character.
			 Then, it stores year, month, and day in the datearray variable.*/
			var datearray = splitdatetime[0].split("-");

			//Likewise, it splits (on the colon charcter) the time parts into hour, minute, and second.
			var timearray = splitdatetime[1].split(":");

			//Checks if datearray has three elements.
			if(datearray.length == 3){
				/*If so, then it means that isodate was in the correct format (i.e. YYYY-MM-DD HH:mm:ss).
				Then, we create a new date object. The parameters are, respectively: year, month (0-based), day, hour, minutes and seconds.*/

				var dateobj = new Date(datearray[0], datearray[1]-1, datearray[2] , timearray[0] , timearray[1] , timearray[2]);

				if(dateobj){
					//If datobj was successfully created, then it is returned.
					return dateobj;
				}
			}
		}
		return null;
	}

	/**
	 * Convert ISO DateTime string to a JS Date object
	 *
	 * @param {string} isodate DateTime string of the form YYYY-MM-DD HH:mm:SS
	 * @returns {Date}
	 */
	getDateObjectFromISODate(isodate) {
		if (!isodate) return

		const dateAndTime = isodate.split(' ')

		if (dateAndTime.length > 1) {
			const dateParts = dateAndTime[0].split('-')
			const timeParts = dateAndTime[1].split(':')

			if (dateParts.length !== 3 || timeParts.length < 3) return

			dateParts[1]--

			return new Date(...dateParts, ...timeParts)
		}

		const dateParts = isodate.split('-')
		if (dateParts.length !== 3) return
		dateParts[1]--

		return new Date(...dateParts)
	}

	/**
	 * Convert ISO DateTime string to a valid input field value for a datetime-local HTML input
	 *
	 * @param {string} isodate DateTime string of the form YYYY-MM-DD HH:mm:SS
	 * @returns {string}
	 */
	getDateTimeFieldValueFromISODate(isodate) {
		const dateAndTime = isodate.split(' ')

		const dateParts = dateAndTime[0].split('-')
		const timeParts = dateAndTime[1].split(':')

		if (dateParts.length !== 3 || timeParts.length < 3) return

		return `${dateParts[0]}-${dateParts[1]}-${dateParts[2]}T${timeParts[0]}:${timeParts[1]}`
	}

	getDateTimeFieldValueFromTimestamp(timestamp, isUTC=true) {
		//Takes a timestamp value as input and returns a formatted date and time string.
		var dateobj = this.getDateObjectFromTimeStamp(timestamp);

		/*We subtract the timezone offset from the date's minutes value.
		The getTimezoneOffset function returns the timezone offset in minutes for the current locale,
		 so subtracting it from the dateobj's minutes value guarantees that the date object represents the correct time in the local timezone. */
		if(isUTC){
			dateobj.setMinutes(dateobj.getMinutes() - dateobj.getTimezoneOffset());
		}

		///We get rid ob seconds and milliseconds for brevity's sake
		dateobj.setSeconds(null);
		dateobj.setMilliseconds(null);

		/*Formats dateobj as an ISO 8601 string using the toISOString function, which returns a string in the format of "YYYY-MM-DDTHH:mm:ss.sssZ".
		 The slice function is then used to remove the last character from the string (the "Z" character),
		 resulting in a string in the format of "YYYY-MM-DDTHH:mm:ss.sss". The formatted string is then returned as the output of the function. */
		return dateobj.toISOString().slice(0, -1);
	}

	getDateFieldValueFromTimestamp(timestamp) {
		/*We extract the date and time from the timestamp. Then, we match the letter 'T' followed by any number of characters and replace it
		with a blank space. This effectively removes the time portion of the date-time value. */
		return this.getDateTimeFieldValueFromTimestamp(timestamp).replace(/T.*/ ,  '');
	}

	/*This returns a string representation of the input timestamp in a localized format using this.i18n.d().
	The this.getDateObjectFromTimeStamp(timestamp) method is called first to convert the timestamp into a JavaScript Date object,
	 which can be formatted into a string. The second parameter, "datetime", specifies the type of formatting desired. */
	getLocaleStringFromTimeStamp(timestamp, isUTC=true) {
		//Takes a timestamp value as input and returns a formatted date and time string.
		var dateobj = this.getDateObjectFromTimeStamp(timestamp);

		if(dateobj){
			/*i18n.d() already converts to the browser's local timezone via Intl.DateTimeFormat.
			So when isUTC is true (the timestamp's UTC calendar fields ARE the intended wall-clock value,
			with no real timezone attached), we ask Intl to format in the UTC timezone instead of
			pre-shifting the Date object, which would double-apply the timezone conversion. */
			return isUTC
				? this.i18n.d(dateobj, {key: "datetime", timeZone: "UTC"})
				: this.i18n.d(dateobj, "datetime");
		}
		//Otherwise, it returns an empty string
		return '';
	}

	/*This returns a string representation of the input timestamp in a localized DATE format using this.i18n.d().
	The this.getDateObjectFromTimeStamp(timestamp) method is called first to convert the timestamp into a JavaScript Date object,
	 which can be formatted into a string. The second parameter, "datetime", specifies the type of formatting desired. */
	getLocaleDateStringFromTimestamp(timestamp, isUTC=true) {
		//Takes a timestamp value as input and returns a formatted date and time string.
		var dateobj = this.getDateObjectFromTimeStamp(timestamp);

		if(dateobj){
			/*i18n.d() already converts to the browser's local timezone via Intl.DateTimeFormat.
			So when isUTC is true (the timestamp's UTC calendar fields ARE the intended wall-clock value,
			with no real timezone attached), we ask Intl to format in the UTC timezone instead of
			pre-shifting the Date object, which would double-apply the timezone conversion. */
			return isUTC
				? this.i18n.d(dateobj, {key: "date", timeZone: "UTC"})
				: this.i18n.d(dateobj, "date");
		}
		//Otherwise, it returns an empty string
		return '';
	}

	/*This returns a string representation of the input timestamp in a localized DATE format using this.i18n.d().
	The this.getDateObjectFromTimeStamp(timestamp) method is called first to convert the timestamp into a JavaScript Date object,
	 which can be formatted into a string. The second parameter, "datetime", specifies the type of formatting desired. */
	getMonthYearStringFromTimestamp(timestamp, isUTC=true) {
		//Takes a timestamp value as input and returns a formatted date and time string.
		var dateobj = this.getDateObjectFromTimeStamp(timestamp);

		if(dateobj){
			/*i18n.d() already converts to the browser's local timezone via Intl.DateTimeFormat.
			So when isUTC is true (the timestamp's UTC calendar fields ARE the intended wall-clock value,
			with no real timezone attached), we ask Intl to format in the UTC timezone instead of
			pre-shifting the Date object, which would double-apply the timezone conversion. */
			return isUTC
				? this.i18n.d(dateobj, {key: "monthyear", timeZone: "UTC"})
				: this.i18n.d(dateobj, "monthyear");
		}
		//Otherwise, it returns an empty string
		return '';
	}

	/*This returns a string representation of the input timestamp in a localized TIME format using this.i18n.d().
	The this.getDateObjectFromTimeStamp(timestamp) method is called first to convert the timestamp into a JavaScript Date object,
	 which can be formatted into a string. The second parameter, "datetime", specifies the type of formatting desired. */
	getLocaleTimeStringFromTimestamp(timestamp, isUTC=true, includeSeconds=false) {
		//Takes a timestamp value as input and returns a formatted date and time string.
		var dateobj = this.getDateObjectFromTimeStamp(timestamp);

		if(dateobj){
			/*i18n.d() already converts to the browser's local timezone via Intl.DateTimeFormat.
			So when isUTC is true (the timestamp's UTC calendar fields ARE the intended wall-clock value,
			with no real timezone attached), we ask Intl to format in the UTC timezone instead of
			pre-shifting the Date object, which would double-apply the timezone conversion. */
			var key = includeSeconds ? "timeWithSeconds" : "time";
			return isUTC
				? this.i18n.d(dateobj, {key, timeZone: "UTC"})
				: this.i18n.d(dateobj, key);
		}
		//Otherwise, it returns an empty string
		return '';
	}

	getRelativeStringFromTimeStamp(timestamp) {
		const hoursSince = ((new Date)/1000 - timestamp) / (60 * 60)

		if (hoursSince >= 24) return this.getLocaleStringFromTimeStamp(timestamp)
		if (hoursSince >= 1) return this.i18n.t('n-hours-ago', { n: Math.floor(hoursSince) })

		const minutesSince = hoursSince * 60

		if (minutesSince >= 1) return this.i18n.tc('n-minutes-ago', Math.floor(minutesSince))
		return this.i18n.t('just-now')
	}

	getLocaleStringFromISODate(isodate, type="date") {
		//First, we convert the ISO date string into a JavaScript date object.
		var dateobj = this.getDateObjectFromISODate(isodate);

		//If it is a valid date, we fortmat is as a string using the user's locale
		if(dateobj){
			return this.i18n.d(dateobj, type);
		}
		//Otherwise, it returns an empty string
		return '';
	}

	getDateObjectFromDateTimeField(value) {
		/*We take the input and replace any space character with a "T" character.
		This is due to the fact that 'Date' expects the string to be in
		ISO 8601 format, which uses the "T" character to separate the date and time components. */
		var dateobj = new Date(value.replace(/ /, "T"));
		return dateobj;
	}

	getTimeStampFromDateTimeField(value, isUTC=true) {
		//We get a Date object containing the date and time
		var dateobj = this.getDateObjectFromDateTimeField(value);
		if(!isUTC){
			dateobj.setMinutes(dateobj.getMinutes() - dateobj.getTimezoneOffset());
		}
		//Convert the miliseconds to seconds and round to get a Unix timestamp
		return Math.round(dateobj.getTime() / 1000);
	}

	getDateTimeLocalFieldNowValue() {
		//Initialize a Date object
		const now = new Date();

		//we set the minutes to UTC
		now.setMinutes(now.getMinutes() - now.getTimezoneOffset());

		//Get rid of seconds and miliseconds
		now.setSeconds(null);
		now.setMilliseconds(null);

		/*Formats the now date object as an ISO string in the format "YYYY-MM-DDTHH:mm:ss.sssZ.
		Then, removes the "Z" at the end of the string so we get a "datetime-local" format,
		this cna be used in an HTML input element*/
		return now.toISOString().slice(0, -1);
	}

	getDateFieldNowValue() {
		//Initialize a Date object
		const now = new Date();
		/*Formats the now date object as an ISO string in the format "YYYY-MM-DDTHH:mm:ss.sssZ.
		Then, removes the "Z" at the end of the string so we get a "datetime-local" format,
		this cna be used in an HTML input element*/
		return now.toISOString().slice(0, 10)
	}

	isToday(ts) {
		//Takes a unix timestamp and returns a boolean depending on whether it corresponds to the current day or not.
		const someDate = new Date(ts * 1000);
		const today = new Date();
		return (
			someDate.getDate() == today.getDate() &&
			someDate.getMonth() == today.getMonth() &&
			someDate.getFullYear() == today.getFullYear()
		);
	}

	isTommorow(ts) {
		//Takes a unix timestamp and returns a boolean depending on whether it corresponds to the next day or not.
		const someDate = new Date(ts * 1000);
		const tommorow = new Date();
		tommorow.setDate(tommorow.getDate() + 1);

		return (
			someDate.getDate() == tommorow.getDate() &&
			someDate.getMonth() == tommorow.getMonth() &&
			someDate.getFullYear() == tommorow.getFullYear()
		);
	}

	isThisWeek(ts) {
		//Takes a unix timestamp and returns a boolean depending on whether it corresponds to the current week or not.
		const someDate = new Date(ts * 1000);
		const weekEnd = new Date();
		weekEnd.setDate(weekEnd.getDate() + 7);

		return (
			someDate.getDate() <= weekEnd.getDate() &&
			someDate.getMonth() <= weekEnd.getMonth() &&
			someDate.getFullYear() <= weekEnd.getFullYear()
		);
	}

    timestampToDate(timestamp, useAmericanFormat = false) {
        /* Takes a Unix timestamp, converts it to a Date object, and returns
		a string that represents the date portion of that Date object in the browser's local time zone.*/
        const ms = timestamp * 1000;
        const dateObj = new Date(ms);
    
        const locale = useAmericanFormat ? 'en-US' : 'en-GB';
    
        return dateObj.toLocaleDateString(locale);
    }

	addZero(i) {
		//Adds a zero if the number is less than 10. If not, it returns the number unchanged.
		if (i < 10) {
			i = "0" + i;
		}
		return i;
	}

	isoDateTimeToTime(isodate) {
		//First, we take an ISO dateime and convert it do a Date object.
		const dateObj = this.getDateObjectFromISODateTime(isodate);

		//If the conversion was succesful, then we make sure that both hours and minute have double digits.
		if(dateObj){
			return this.addZero(dateObj.getHours()) + ":" + this.addZero(dateObj.getMinutes());
		}

		//Otherwise, it returns a null
		return null;
	}

	timestampToTime(timestamp, isutc=true) {
		//This converts a Unix timestamp (in seconds) to a time string in the format of "hh:mm"
		const ms = timestamp * 1000;
		const dateObj = new Date(ms);
		if(!isutc){
			/* If isutc is not true (which is the default), then adjust the date object's minutes
			 to account for the timezone offset. This is done by adding the getTimezoneOffset()
			 to the current minutes. */
			dateObj.setMinutes(dateObj.getMinutes() + dateObj.getTimezoneOffset());
		}
		//We make sure that both hours and minute have double digits.
		return this.addZero(dateObj.getHours()) + ":" + this.addZero(dateObj.getMinutes());
	}

	timestampToDay(timestamp) {
		//Takes a timestamp and returns the corresponding day of the week
		const ms = timestamp * 1000;
		const dateObj = new Date(ms);

		const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

		return days[dateObj.getDay()];
	}
}

Date.prototype.getWeekOfMonth = function(exact) {
	var month = this.getMonth()
		, year = this.getFullYear()
		, firstWeekday = new Date(year, month, 1).getDay()
		, lastDateOfMonth = new Date(year, month + 1, 0).getDate()
		, offsetDate = this.getDate() + firstWeekday - 1
		, index = 1 // start index at 0 or 1, your choice
		, weeksInMonth = index + Math.ceil((lastDateOfMonth + firstWeekday - 7) / 7)
		, week = index + Math.floor(offsetDate / 7)
	;
	if (exact || week < 2 + index) return week;
	return week === weeksInMonth ? index + 5 : week;
};
